import { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import type { OrderWithDetails, MenuItem, OrderItemWithDetails } from '@/types/common';
import { useOrderData } from '@/hooks/useOrderData';
import { useOrderOperations } from '@/hooks/useOrderOperations';
import { useProfiler } from '@/hooks/useProfiler';
import { useToast } from '@/contexts/ToastContext';
import MenuSection from '@/components/Order/MenuSection';
import OrderSummary from '@/components/Order/OrderSummary';
import { supabase } from '@/lib/supabase';
import { formatCurrency } from '@/utils/format';
import { Profiler } from 'react';

export default function OrderPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const [searchParams] = useSearchParams();
  const [selectedTable, setSelectedTable] = useState<string>(searchParams.get('table') || '');
  const [hasItems, setHasItems] = useState(false);
  const navigate = useNavigate();
  const { addToast } = useToast();
  const onRender = useProfiler('OrderPage');
  
  // Fetch order and menu data
  const {
    order,
    tables,
    categories,
    loading,
    error,
    retry,
    retrying,
    updateOrderLocally,
  } = useOrderData(orderId);

  // Update selected table when order is loaded
  useEffect(() => {
    if (order?.table_id) {
      setSelectedTable(order.table_id);
    }
  }, [order]);

  // Update hasItems when order changes
  useEffect(() => {
    setHasItems(Boolean(order?.items?.length));
  }, [order]);

  // Modify the real-time subscription to handle local updates
  useEffect(() => {
    if (!orderId) return;

    const channel = supabase.channel(`order-${orderId}`);

    channel
      .on(
        'postgres_changes',
        { 
          event: '*', 
          schema: 'public', 
          table: 'order_items',
          filter: `order_id=eq.${orderId}`
        },
        (payload) => {
          // Update local state directly instead of re-fetching
          if (order) {
            if (payload.eventType === 'INSERT') {
              const newItem = payload.new as OrderItemWithDetails;
              updateOrderLocally({
                ...order,
                items: [...order.items, newItem],
                total: order.total + (newItem.quantity * newItem.menu_item.price)
              });
            } else if (payload.eventType === 'UPDATE') {
              const updatedItem = payload.new as OrderItemWithDetails;
              const oldItem = order.items.find(item => item.id === payload.old.id);
              const priceDiff = oldItem 
                ? (updatedItem.quantity - oldItem.quantity) * oldItem.menu_item.price
                : 0;

              updateOrderLocally({
                ...order,
                items: order.items.map(item =>
                  item.id === updatedItem.id ? updatedItem : item
                ),
                total: order.total + priceDiff
              });
            } else if (payload.eventType === 'DELETE') {
              const deletedItem = order.items.find(item => item.id === payload.old.id);
              const priceReduction = deletedItem 
                ? deletedItem.quantity * deletedItem.menu_item.price
                : 0;

              updateOrderLocally({
                ...order,
                items: order.items.filter(item => item.id !== payload.old.id),
                total: order.total - priceReduction
              });
            }
          }
        }
      )
      .subscribe();

    return () => {
      void channel.unsubscribe();
    };
  }, [orderId, order, updateOrderLocally]);

  // Memoize the update callback
  const handleOrderUpdate = useCallback(async (updatedOrder: OrderWithDetails) => {
    updateOrderLocally(updatedOrder);
  }, [updateOrderLocally]);

  // Order operations (add/update items)
  const {
    saving,
    addOrderItem,
    updateItemQuantity
  } = useOrderOperations({
    order,
    onOrderUpdate: handleOrderUpdate
  });

  // Remove animation classes, keep only the width logic
  const menuListClass = hasItems ? 'w-full sm:w-[65%]' : 'w-full';

  const orderSummaryClass = `
    fixed top-14 right-0 h-[calc(100vh-56px)] bg-white dark:bg-dark-bg-light
    hidden sm:block
    ${hasItems ? 'sm:w-[35%]' : 'w-0'}
    shadow-lg border-l border-gray-200 dark:border-dark-border
    overflow-hidden
    rounded-l-2xl
  `;

  const handleAddItem = (item: MenuItem) => {
    if (!hasItems) {
      setHasItems(true);
    }
    addOrderItem(item, selectedTable);
  };

  const handleProceedToPayment = () => {
    if (order) {
      navigate(`/payment/${order.id}`);
    }
  };

  const handleDeleteOrder = async () => {
    if (!order || !order.table_id) return;

    try {
      // Delete order items first
      const { error: itemsError } = await supabase
        .from('order_items')
        .delete()
        .eq('order_id', orderId);

      if (itemsError) throw itemsError;

      // Delete the order
      const { error: orderError } = await supabase
        .from('orders')
        .delete()
        .eq('id', orderId);

      if (orderError) throw orderError;

      // Update table status to available
      const { error: tableError } = await supabase
        .from('tables')
        .update({ status: 'available' })
        .eq('id', order.table_id);

      if (tableError) throw tableError;

      addToast({
        type: 'success',
        message: 'Order deleted successfully'
      });

      // Reset hasItems and navigate to new order page
      setHasItems(false);
      navigate('/orders/new');
    } catch (error) {
      console.error('Error deleting order:', error);
      addToast({
        type: 'error',
        message: 'Failed to delete order'
      });
    }
  };

  if (error) {
    return (
      <Profiler id="OrderPage-Error" onRender={onRender}>
        <div className="flex flex-col items-center justify-center min-h-screen-75 text-gray-600 dark:text-dark-text-secondary">
          <i className="fas fa-exclamation-circle text-4xl mb-4 text-red-500" />
          <h3 className="text-lg font-medium mb-2">Failed to load data</h3>
          <p className="text-sm mb-4">{error.message}</p>
          <button
            onClick={retry}
            disabled={retrying}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
          >
            {retrying ? (
              <>
                <i className="fas fa-spinner fa-spin mr-2" />
                Retrying...
              </>
            ) : (
              <>
                <i className="fas fa-redo mr-2" />
                Try Again
              </>
            )}
          </button>
        </div>
      </Profiler>
    );
  }

  if (loading) {
    return (
      <Profiler id="OrderPage-Loading" onRender={onRender}>
        <div className="flex flex-col items-center justify-center min-h-screen-75">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary-600 mb-4"></div>
          <p className="text-gray-600 dark:text-dark-text-secondary">Loading...</p>
        </div>
      </Profiler>
    );
  }

  return (
    <Profiler id="OrderPage" onRender={onRender}>
      <div className="h-[calc(100vh-56px)] flex flex-col sm:flex-row hide-scrollbar">
        {/* Menu Section */}
        <div className={`${hasItems ? 'sm:w-[65%]' : 'w-full'} h-full`}>
          <MenuSection
            selectedTable={selectedTable}
            tables={tables}
            categories={categories}
            onSelectTable={setSelectedTable}
            onAddItem={handleAddItem}
            saving={saving}
            orderId={orderId}
          />
        </div>

        {/* Desktop Order Summary */}
        {hasItems && order && (
          <aside className="hidden sm:block sm:w-[35%] border-l border-gray-200 dark:border-dark-border bg-white dark:bg-dark-bg h-full overflow-auto">
            <OrderSummary
              items={order.items}
              onQuantityChange={updateItemQuantity}
              onProceedToPayment={handleProceedToPayment}
              saving={saving}
              isDesktop={true}
              onDeleteOrder={handleDeleteOrder}
              tableNumber={order.table?.number}
            />
          </aside>
        )}

        {/* Mobile Order Summary */}
        {hasItems && order && (
          <div className="block sm:hidden fixed bottom-0 left-0 right-0 z-50">
            <OrderSummary
              items={order.items}
              onQuantityChange={updateItemQuantity}
              onProceedToPayment={handleProceedToPayment}
              saving={saving}
              isDesktop={false}
              onDeleteOrder={handleDeleteOrder}
              tableNumber={order.table?.number}
            />
          </div>
        )}
      </div>
    </Profiler>
  );
}
